import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { peopleGateway } from '../../index';
import { IdentityContext } from '../../../identity/state/IdentityContext';
import { usePermissionCheck } from '../../../identity/state/usePermissions';
import { Modal } from '../../../check-batch/components/Modal';
import '../../../check-batch/check-batch.css';
import { stateName } from '../../stateNames';
import { ELECTION_PERMISSIONS, MAX_NOTE, acceptsElections, catalogMessage, describeAnswers, electionErrorMessage, formatDate } from '../../stateElections';
import { StateElectionForm } from '../../components/StateElectionForm';
import type {
  EmployeeResponse,
  StateCertificateEntry,
  StateWithholdingAnswers,
  StateWithholdingCatalogResponse,
  StateWithholdingElectionsResponse,
  StateWithholdingStateView,
  StateWithholdingSubmissionDetail,
} from '../../types';
import type { AdminStateTaxState } from './useAdminStateTax';
import { WorkLocationSection } from './EmployeeWorkSections';
import { EmployeeAddressSection } from './EmployeeAddressSection';

const ASK_ADMIN = 'Ask an administrator for access.';

const STATUS_WORDS: Record<StateWithholdingSubmissionDetail['status'], string> = {
  SubmittedPendingReview: 'Waiting for review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Withdrawn: 'Withdrawn',
};

/** Who did it, in words. Ids are never shown; the reviewer only needs to know whether it was them. */
const who = (userId: string | null, me: string | undefined) => (userId === null ? '' : userId === me ? 'you' : 'another user');

function Answers({ entry, answers, testId }: { entry: StateCertificateEntry | undefined; answers: StateWithholdingAnswers; testId: string }) {
  return (
    <dl className="sit__dl sit__dl--compact" data-testid={testId}>
      {describeAnswers(entry, answers).map((r) => (
        <div key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The form for one state (the shared catalog-driven form). It stays mounted while a dependency dialog is open, so nothing typed is lost. */
function ElectionFormCard({
  employeeId,
  stateCode,
  entry,
  onSubmitted,
  onCancel,
}: {
  employeeId: string;
  stateCode: string;
  entry: StateCertificateEntry;
  onSubmitted: (message: string) => void;
  onCancel: () => void;
}) {
  return (
    <StateElectionForm
      stateCode={stateCode}
      entry={entry}
      audience="admin"
      send={(input) => peopleGateway.submitStateWithholdingElection(employeeId, input)}
      onSubmitted={onSubmitted}
      onCancel={onCancel}
    />
  );
}

/** Review of an open submission (approve, or reject with a reason). The person who entered it sees why they cannot review it. */
function ReviewPanel({
  employeeId,
  entry,
  submission,
  meUserId,
  canApprove,
  canWrite,
  onChanged,
}: {
  employeeId: string;
  entry: StateCertificateEntry | undefined;
  submission: StateWithholdingSubmissionDetail;
  meUserId: string | undefined;
  canApprove: boolean;
  canWrite: boolean;
  onChanged: (message: string) => void;
}) {
  const [decision, setDecision] = useState<'Approve' | 'Reject' | ''>('');
  const [note, setNote] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const name = stateName(submission.stateCode);
  const mine = meUserId !== undefined && submission.submittedByUserId === meUserId;

  async function record() {
    if (!decision) return setProblem('Choose Approve or Reject.');
    if (decision === 'Reject' && !note.trim()) return setProblem('Give a reason for rejecting the submission.');
    if (note.length > MAX_NOTE) return setProblem(`The note must be ${MAX_NOTE} characters or fewer.`);
    if (expiresOn && expiresOn <= submission.effectiveFrom) return setProblem('The expiry date must be after the date the election takes effect.');
    setSaving(true);
    setProblem(null);
    try {
      await peopleGateway.reviewStateWithholdingElection(employeeId, submission.submissionId, {
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(decision === 'Approve' && expiresOn ? { expiresOn } : {}),
        concurrencyVersion: submission.concurrencyVersion,
      });
      onChanged(decision === 'Approve' ? `${name} election approved.` : `${name} submission rejected.`);
    } catch (err) {
      setProblem(electionErrorMessage(err, 'The decision could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function withdraw() {
    setSaving(true);
    setProblem(null);
    try {
      await peopleGateway.withdrawStateWithholdingElection(employeeId, submission.submissionId);
      onChanged(`${name} submission withdrawn.`);
    } catch (err) {
      setProblem(electionErrorMessage(err, 'The submission could not be withdrawn. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="recip-review__card" aria-label={`${name} submission waiting for review`} data-testid={`se-open-${submission.stateCode}`}>
      <header className="recip-review__head">
        <h5>Waiting for review</h5>
        <span className="recip-review__chip recip-review__chip--SubmittedPendingReview">Not used for payroll yet</span>
      </header>
      <p className="muted">
        Takes effect {formatDate(submission.effectiveFrom)}. Entered by {who(submission.submittedByUserId, meUserId)} on {formatDate(submission.submittedAtUtc)}.
      </p>
      <Answers entry={entry} answers={submission.answers} testId={`se-open-answers-${submission.stateCode}`} />
      {!canApprove ? (
        <p data-testid="se-no-approve">You can&apos;t review state withholding elections. {ASK_ADMIN}</p>
      ) : mine ? (
        <p className="recip-review__warning" data-testid="se-self">
          You entered this submission, so another reviewer must review it.
        </p>
      ) : (
        <div className="recip-review__form">
          <fieldset className="recip-review__decision">
            <legend>Decision</legend>
            <label>
              <input type="radio" name={`se-decision-${submission.submissionId}`} checked={decision === 'Approve'} onChange={() => setDecision('Approve')} /> Approve
            </label>
            <label>
              <input type="radio" name={`se-decision-${submission.submissionId}`} checked={decision === 'Reject'} onChange={() => setDecision('Reject')} /> Reject
            </label>
          </fieldset>
          {decision === 'Approve' && submission.answers.isExempt && (
            <label className="recip-review__field">
              Exemption expires on (optional)
              <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </label>
          )}
          <label className="recip-review__field">
            {decision === 'Reject' ? 'Reason (required)' : 'Note (optional)'}
            <textarea rows={2} maxLength={MAX_NOTE} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="recip-review__dialog-actions">
            <button type="button" onClick={() => void record()} disabled={saving}>
              {saving ? 'Saving…' : 'Record decision'}
            </button>
          </div>
        </div>
      )}
      {canWrite && (
        <button type="button" className="sit__link" onClick={() => void withdraw()} disabled={saving}>
          Withdraw this submission
        </button>
      )}
      {problem && (
        <p className="error" role="alert">
          {problem}
        </p>
      )}
    </article>
  );
}

function StateCard({
  employeeId,
  stateCode,
  entry,
  view,
  meUserId,
  canWrite,
  canApprove,
  onChanged,
}: {
  employeeId: string;
  stateCode: string;
  entry: StateCertificateEntry | undefined;
  view: StateWithholdingStateView | undefined;
  meUserId: string | undefined;
  canWrite: boolean;
  canApprove: boolean;
  onChanged: (message: string) => void;
}) {
  const [entering, setEntering] = useState(false);
  const name = stateName(stateCode);
  const takes = entry !== undefined && acceptsElections(entry);
  const open = view?.openSubmission ?? null;

  return (
    <section className="sit__block" aria-labelledby={`se-${stateCode}-title`} data-testid={`se-state-${stateCode}`}>
      <h4 id={`se-${stateCode}-title`} className="sit__block-title">
        {name}
      </h4>
      <p className="muted">{catalogMessage(entry)}</p>

      {view?.current ? (
        <div data-testid={`se-current-${stateCode}`}>
          <p>
            <strong>In effect</strong> since {formatDate(view.current.effectiveFrom)}
            {view.current.expiresOn ? `, until ${formatDate(view.current.expiresOn)}` : ''}.
          </p>
          <Answers entry={entry} answers={view.current.answers} testId={`se-current-answers-${stateCode}`} />
        </div>
      ) : takes ? (
        <p data-testid={`se-none-${stateCode}`}>No election is in effect for {name}.</p>
      ) : null}

      {view?.upcoming && (
        <p data-testid={`se-upcoming-${stateCode}`}>
          A newer election is approved and takes effect {formatDate(view.upcoming.effectiveFrom)}.
        </p>
      )}

      {open && (
        <ReviewPanel
          employeeId={employeeId}
          entry={entry}
          submission={open}
          meUserId={meUserId}
          canApprove={canApprove}
          canWrite={canWrite}
          onChanged={onChanged}
        />
      )}

      {takes && !open && entry &&
        (!canWrite ? (
          <p data-testid={`se-no-write-${stateCode}`}>You can&apos;t enter state withholding elections. {ASK_ADMIN}</p>
        ) : entering ? (
          <ElectionFormCard
            employeeId={employeeId}
            stateCode={stateCode}
            entry={entry}
            onCancel={() => setEntering(false)}
            onSubmitted={(m) => {
              setEntering(false);
              onChanged(m);
            }}
          />
        ) : (
          <button type="button" onClick={() => setEntering(true)}>
            Enter {name} election
          </button>
        ))}

      {view && view.history.length > 0 && (
        <details data-testid={`se-history-${stateCode}`}>
          <summary>History ({view.history.length})</summary>
          <ul className="sit__fields">
            {view.history.map((h) => (
              <li key={h.submissionId} className="sit__field">
                <span>
                  <strong>{STATUS_WORDS[h.status]}</strong> · takes effect {formatDate(h.effectiveFrom)} · entered by {who(h.submittedByUserId, meUserId)}{' '}
                  {h.source === 'Employee' ? '(employee)' : '(administrator)'} on {formatDate(h.submittedAtUtc)}
                  {h.reviewedAtUtc ? ` · reviewed by ${who(h.reviewedByUserId, meUserId)} on ${formatDate(h.reviewedAtUtc)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

type Dependency = 'work' | 'home' | null;

/**
 * EPN-52 state withholding elections, administrator screens (PR 4). One card per state that applies to the employee (work state, residence state, and any
 * state that already has an election), each driven by the controlled catalog: only the fields a state takes are
 * offered. Every submission waits for a different reviewer (SME-34 default); nothing here calculates tax. The work location and home address that decide
 * which states apply can be changed in place: the existing sections open in a dialog over this page, with their own API, validation and permissions.
 * UX / DESIGN GAP: no Figma frames exist for entry, review or history; the layout reuses the reciprocity review and the W-4 history patterns.
 */
export function StateElectionsSection({ employee, stateTax }: { employee: EmployeeResponse; stateTax: AdminStateTaxState }) {
  const can = usePermissionCheck();
  const meUserId = useContext(IdentityContext)?.me?.userId;
  const canRead = can(ELECTION_PERMISSIONS.read);
  const canWrite = can(ELECTION_PERMISSIONS.write);
  const canApprove = can(ELECTION_PERMISSIONS.approve);

  const [catalog, setCatalog] = useState<StateWithholdingCatalogResponse | null>(null);
  const [elections, setElections] = useState<StateWithholdingElectionsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [dependency, setDependency] = useState<Dependency>(null);
  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!canRead) return undefined;
    let cancelled = false;
    Promise.all([peopleGateway.getStateWithholdingCatalog(), peopleGateway.getStateWithholdingElections(employee.id)])
      .then(([c, e]) => {
        if (cancelled) return;
        setCatalog(c);
        setElections(e);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(electionErrorMessage(err, "This employee's state withholding elections could not be loaded. Please try again."));
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id, canRead, attempt]);

  const reload = useCallback(() => {
    setLoadError(null);
    setAttempt((n) => n + 1);
  }, []);

  const changed = useCallback(
    (m: string) => {
      setMessage(m);
      reload();
      // Keep keyboard users oriented after the card they were in re-renders.
      setTimeout(() => messageRef.current?.focus(), 0);
    },
    [reload],
  );

  function closeDependency() {
    setDependency(null);
    // The work or residence state may have changed: read both again.
    stateTax.reload();
    reload();
  }

  // The last state picture read: a reload (after a dependency dialog) briefly clears it, and the cards, with whatever the user is typing, must stay mounted.
  const [tax, setTax] = useState(stateTax.data);
  if (stateTax.data && stateTax.data !== tax) setTax(stateTax.data);
  // multiState.states carries display names, so the codes come from the work and residence states only.
  const derived = tax ? [tax.workState.code, tax.residenceState.code] : [];
  const codes = [...new Set([...derived, ...(elections?.states.map((s) => s.stateCode) ?? [])].filter((c): c is string => !!c))].sort();

  return (
    <section className="profile-section sit" aria-labelledby="se-title" data-testid="state-elections">
      <h3 id="se-title" className="profile-section-title">
        State withholding elections
      </h3>

      {!canRead ? (
        <p data-testid="se-no-read">Your role can&apos;t view state withholding elections. {ASK_ADMIN}</p>
      ) : loadError ? (
        <>
          <p className="error" role="alert">
            {loadError}
          </p>
          <button type="button" onClick={reload}>
            Try again
          </button>
        </>
      ) : catalog === null || elections === null || (tax === null && !stateTax.failed) ? (
        <p className="muted" role="status">
          Loading state withholding elections…
        </p>
      ) : (
        <>
          <p className="muted" data-testid="se-intro">
            Record each state&apos;s withholding election here. Another reviewer must approve it before it is used, and approved elections are used only by the staging
            tax preview. The list of what each state&apos;s form asks is a draft waiting for a payroll tax specialist to approve it.
          </p>
          <p ref={messageRef} tabIndex={-1} role="status" aria-live="polite" className="sit__live">
            {message}
          </p>

          <div className="sit__block" data-testid="se-dependencies">
            <p>
              Which states need an election depends on where the employee works and lives.
              {tax?.workState.status === 'Unknown' ? ' There is no work state yet.' : ''}
              {tax?.residenceState.status === 'Unknown' ? ' There is no residence state yet.' : ''}
            </p>
            <div className="recip-review__dialog-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="sit__link" onClick={() => setDependency('work')}>
                {tax?.workState.status === 'Unknown' ? 'Assign a primary work location' : 'Change the work location'}
              </button>
              <button type="button" className="sit__link" onClick={() => setDependency('home')}>
                {tax?.residenceState.status === 'Unknown' ? 'Add a home address' : 'Change the home address'}
              </button>
            </div>
          </div>

          {codes.length === 0 ? (
            <p data-testid="se-empty">No state applies to this employee yet. Assign a work location or add a home address first.</p>
          ) : (
            codes.map((code) => (
              <StateCard
                key={code}
                employeeId={employee.id}
                stateCode={code}
                entry={catalog.states.find((s) => s.code === code)}
                view={elections.states.find((s) => s.stateCode === code)}
                meUserId={meUserId}
                canWrite={canWrite}
                canApprove={canApprove}
                onChanged={changed}
              />
            ))
          )}
        </>
      )}

      {dependency && (
        <Modal titleId="se-dependency-title" onClose={closeDependency} closeLabel="Close and return to state elections">
          <h3 id="se-dependency-title">{dependency === 'work' ? 'Work location' : 'Home address'}</h3>
          <p className="muted">Saving here updates the employee record. Close this to return to state elections; nothing you entered there is lost.</p>
          {dependency === 'work' ? <WorkLocationSection employee={employee} /> : <EmployeeAddressSection employee={employee} />}
          <div className="recip-review__dialog-actions">
            <button type="button" onClick={closeDependency}>
              Done
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
